import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Body2, Dialog, Button } from "@activecollab/components";
import PropTypes from "prop-types";
import { useLinkContext } from "~/components/LinkProvider/context";
import OrderSummary from "~/components/Subscription/OrderSummary";
import { fetchSubscriptionBalance } from "~/store/actions/actions.subscriptionBalance";
import { getIsPerSeatPricingModel } from "~/store/selectors/selector.accountSettings";
import { getBillingOrder, isSwitchToAnnual } from "~/store/selectors/selector.billingOrder";
import {
  getPaymentMethodCreditCard,
  isPaymentMethodAvailable as getIsPaymentMethodAvailable,
  isPrePaidPaymentMethod as getIsPrePaidPaymentMethod,
} from "~/store/selectors/selector.billingPaymentMethod";
import { getSubscriptionAddOnByNameSelector } from "~/store/selectors/selector.subscriptionAddons";
import { getSubscriptionPlanByNameSelector } from "~/store/selectors/selector.subscriptionPlans";

const NO_PAYMENT_METHOD = "no-payment-method";
const INSUFFICIENT_FUNDS = "insufficient-funds";
const EXPIRED_CARD = "expired-card";
const LEGACY_PAYMENT_METHOD = "legacy-payment-method";

const OrderSummaryDialog = ({
  open,
  title,
  confirmBtnText,
  onConfirm,
  cancelBtnText,
  onCancel,
  onMissingPaymentMethod,
  onUpdatingCreditCard,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const { liveSupportQueryData } = useLinkContext();

  useEffect(() => {
    if (open) {
      dispatch(fetchSubscriptionBalance());
    }
  }, [dispatch, open]);

  const billingOrder = useSelector(getBillingOrder);
  const isPerSeat = useSelector(getIsPerSeatPricingModel);
  const shouldRenderAnnualSavings = useSelector(isSwitchToAnnual);
  const isPaymentMethodAvailable = useSelector(getIsPaymentMethodAvailable);
  const isPrePaidPaymentMethod = useSelector(getIsPrePaidPaymentMethod);
  const paymentMethod = useSelector(getPaymentMethodCreditCard);
  const creditCard = useSelector(getPaymentMethodCreditCard);
  const subscriptionPlan = useSelector((state) => getSubscriptionPlanByNameSelector(state, billingOrder?.get("plan")));
  const subscriptionAddon = useSelector((state) =>
    getSubscriptionAddOnByNameSelector(state, billingOrder?.get("add_ons"))
  );

  const annualSavings = useMemo(() => {
    let savings = 0;
    if (subscriptionPlan?.annual_savings) {
      savings += subscriptionPlan.annual_savings;
    }
    if (subscriptionAddon?.annual_savings) {
      savings += subscriptionAddon.annual_savings;
    }

    return savings;
  }, [subscriptionAddon, subscriptionPlan]);

  const queryParams = liveSupportQueryData ? `?${liveSupportQueryData}` : "";
  const helpUrl = `https://activecollab.com/help${queryParams}`;

  const [restrictionReason, setRestrictionReason] = useState("");

  const renderRestriction = useMemo(() => {
    if (isPaymentMethodAvailable && paymentMethod.get("type") === "FastSpringPaymentMethod") {
      setRestrictionReason(LEGACY_PAYMENT_METHOD);
      return true;
    }

    if (!isPaymentMethodAvailable && !isPrePaidPaymentMethod) {
      setRestrictionReason(NO_PAYMENT_METHOD);
      return true;
    }

    if (billingOrder?.get("total") <= 0) {
      return false;
    }

    if (isPrePaidPaymentMethod) {
      setRestrictionReason(INSUFFICIENT_FUNDS);

      return true;
    }

    if (creditCard.get("is_expired")) {
      setRestrictionReason(EXPIRED_CARD);

      return true;
    }

    return false;
  }, [billingOrder, creditCard, isPaymentMethodAvailable, isPrePaidPaymentMethod, paymentMethod]);

  const restrictionTitle = useMemo(() => {
    if (restrictionReason === LEGACY_PAYMENT_METHOD) {
      return "We’re changing our payment gateway";
    }

    if (restrictionReason === EXPIRED_CARD) {
      return "Credit Card Expired";
    }

    if (restrictionReason === INSUFFICIENT_FUNDS) {
      return "Insufficient Funds";
    }

    if (restrictionReason === NO_PAYMENT_METHOD) {
      return "No Credit Card";
    }
  }, [restrictionReason]);

  const restrictionExplainer = useMemo(() => {
    if (restrictionReason === LEGACY_PAYMENT_METHOD) {
      return "Please enter your card details once more to proceed with the purchase.";
    }

    if (restrictionReason === EXPIRED_CARD) {
      return `You credit card ending with ${creditCard.get(
        "card_number"
      )} has expired. Please enter a valid payment method.`;
    }

    if (restrictionReason === INSUFFICIENT_FUNDS) {
      return "Your account balance is insufficient. Contact our Support Team to top up your balance or switch to a different payment method.";
    }

    if (restrictionReason === NO_PAYMENT_METHOD) {
      return "Please enter a valid payment method to proceed with your purchase.";
    }
  }, [creditCard, restrictionReason]);

  const primaryRestrictionActionLabel = useMemo(() => {
    return restrictionReason === NO_PAYMENT_METHOD ? "Add Credit Card" : "Add New Card";
  }, [restrictionReason]);

  const primaryRestrictionAction = useCallback(() => {
    return restrictionReason === NO_PAYMENT_METHOD ? onMissingPaymentMethod() : onUpdatingCreditCard();
  }, [onMissingPaymentMethod, onUpdatingCreditCard, restrictionReason]);

  const renderSecondaryRestrictionAction = useMemo(() => {
    return restrictionReason === INSUFFICIENT_FUNDS;
  }, [restrictionReason]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <Dialog.Title>{title}</Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        {renderRestriction ? (
          <>
            <div className="tw-flex">
              <div>
                <Body2
                  color="alert"
                  weight="bold"
                >
                  {restrictionTitle}
                </Body2>
                <Body2 className="tw-mt-2">{restrictionExplainer}</Body2>
                <Body2 className="tw-mt-2">
                  <a
                    href="#"
                    onClick={primaryRestrictionAction}
                    className="activity-link"
                  >
                    {primaryRestrictionActionLabel}
                  </a>
                  {renderSecondaryRestrictionAction ? (
                    <a
                      href={helpUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="tw-ml-4 activity-link"
                    >
                      Contact Support
                    </a>
                  ) : null}
                </Body2>
              </div>
            </div>
            <Dialog.ContentDivider className="tw-my-6" />
          </>
        ) : null}
        {shouldRenderAnnualSavings ? (
          <>
            <Body2
              color="primary"
              lineHeight="loose"
              data-testid="annual-savings-info"
            >
              Switch to the annual plan and save <b>${annualSavings}</b>!
              {isPerSeat ? " You’ll also get an additional storage space of 25GB for your files." : null}
            </Body2>
            <Dialog.ContentDivider className="tw-my-6" />
          </>
        ) : null}
        <OrderSummary canUserChangeBillingPeriod={false} />
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          variant="primary"
          style={{ marginRight: "12px" }}
          onClick={onConfirm}
          disabled={renderRestriction || isLoading}
        >
          {confirmBtnText}
        </Button>
        <Button
          variant="secondary"
          data-action="cancel"
          onClick={onCancel}
          type="button"
          disabled={isLoading}
        >
          {cancelBtnText}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

OrderSummaryDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  confirmBtnText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onMissingPaymentMethod: PropTypes.func.isRequired,
  onUpdatingCreditCard: PropTypes.func.isRequired,
  cancelBtnText: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

OrderSummaryDialog.defaultProps = {
  open: false,
  confirmBtnText: "Purchase",
  cancelBtnText: "Cancel",
  isLoading: false,
};

OrderSummaryDialog.displayName = `OrderSummaryDialog`;

export default OrderSummaryDialog;
